import { loadStripe } from '@stripe/stripe-js';
import React from 'react';
import { Redirect, useParams } from 'react-router-dom';

import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal/Modal';
import {
  PaymentMethod,
  PaymentSessionResult
} from '../../../generated/graphql';
import getEnv from '../../../utils/getEnv';
import { InitiatePaymentParams } from '../InitiatePayment';

/**
 * Stripe API
 */
const {
  REACT_APP_STRIPE_CA_PUBLISHABLE_KEY,
  REACT_APP_STRIPE_US_PUBLISHABLE_KEY
} = getEnv();

const { REACT_APP_MONERIS_ENDPOINT } = getEnv();
const MONERIS_FORM_ID = 'moneris-preprocessing-form';
const MONERIS_HPP_ID_LABEL = 'moneris_hpp_id';
const MONERIS_TICKET_LABEL = 'moneris_ticket';

const SUPPORTED_PAYMENT_METHOD = new Set([PaymentMethod.CreditCard]);

const getInputById = (id: string) => {
  const ret = document.getElementById(id) as HTMLInputElement;
  if (!ret) {
    throw new Error(`bug: input component not found, id: [${id}]`);
  }
  return ret;
};

interface PaymentRedirectModalProps {
  paymentMethod: PaymentMethod;
  paymentSessionResult: PaymentSessionResult | undefined;
  setClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const PaymentRedirectModal: React.FC<PaymentRedirectModalProps> = ({
  paymentMethod,
  paymentSessionResult,
  setClose
}) => {
  const { invoiceId: invoiceIdFromUrl } = useParams<InitiatePaymentParams>();
  if (!SUPPORTED_PAYMENT_METHOD.has(paymentMethod)) {
    throw new Error(`unreachable: ${paymentMethod} not supported`);
  }

  const paymentProvider =
    paymentSessionResult?.__typename === 'MonerisData' ? 'Moneris' : 'Stripe';

  const handlePaymentModalNext = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    // Determine if using Moneris or Stripe session
    switch (paymentSessionResult?.__typename) {
      /**
       * MONERIS AS SERVICE
       */
      case 'MonerisData':
        // Get Moneris tokens from the backend

        // Fill the tokens in a HTML form and use its submission mechanism
        // to starts a Post request to Moneris.
        const form = document.getElementById(
          MONERIS_FORM_ID
        ) as HTMLFormElement;
        if (!form) {
          throw new Error('bug: moneris preprocessing form not found');
        }

        const { hpp_id, ticket } = paymentSessionResult;
        getInputById(MONERIS_HPP_ID_LABEL).value = hpp_id;
        getInputById(MONERIS_TICKET_LABEL).value = ticket;
        form.submit();
        break;
      /**
       * STRIPE AS SERVICE
       */
      case 'StripeData':
        const publishableKey =
          paymentSessionResult.stripePlatformAccount ===
          'StripePlatformAccount-US'
            ? REACT_APP_STRIPE_US_PUBLISHABLE_KEY
            : REACT_APP_STRIPE_CA_PUBLISHABLE_KEY;
        const stripe = await loadStripe(publishableKey);
        if (stripe) {
          // Stripe API call to redirect to a new session
          await stripe.redirectToCheckout({
            sessionId: paymentSessionResult.session_id
          });
        } else {
          throw new Error(`unreachable: Stripe Redirect failed`);
        }
        break;
      default:
        throw new Error(
          'unexpected: expected either MonerisPreloadData or StripePreloadData'
        );
    }
  };

  const PaymentRedirectModal = () => (
    <div
      className="PaymentRedirectModal w-full"
      data-testid="PaymentRedirectModal"
    >
      <Modal
        setClose={setClose}
        onAction={handlePaymentModalNext}
        actionName={'NEXT'}
      >
        <form
          id={MONERIS_FORM_ID}
          method="post"
          action={REACT_APP_MONERIS_ENDPOINT}
          className="hidden"
        >
          <input type="hidden" id={MONERIS_HPP_ID_LABEL} name="hpp_id" />
          <input type="hidden" name="hpp_preload" value="" />
          <input type="hidden" id={MONERIS_TICKET_LABEL} name="ticket" />
        </form>
        <div className="px-8 py-12">
          <p>
            {`${paymentProvider} processes the payments for Foxquilt. After clicking
            NEXT you will be redirected to a secure payment page hosted by
            ${paymentProvider}.`}
          </p>
          <p>Your information will not be shared with any third party.</p>
        </div>
      </Modal>
    </div>
  );

  if (!paymentSessionResult) {
    return <Loading />;
  } else {
    if (
      paymentSessionResult.__typename === 'InactiveRenewalData' ||
      paymentSessionResult.__typename === 'ActiveRenewalData'
    ) {
      return <Redirect to={`/complete/${invoiceIdFromUrl}`} />;
    } else {
      return PaymentRedirectModal();
    }
  }
};

export default PaymentRedirectModal;
